// @owners { team: patients }
import { BORDERS, COLORS, SPACING } from '@alto/design-library-tokens';
import { LgSpacing, ListDescription, ListItem, Separator, SmSpacing } from '@alto/design-system';
import { type LineItem, type LineItemType } from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import React, { Fragment } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import { TypescriptUtils } from '~shared/TypescriptUtils';
import { getOrderDate } from '~shared/features/checkout/selectors/getOrder';
import { getPrescriptionPricing } from '~shared/features/pricing/selectors/getPricing';
import { type OrderPricingQueryData } from '~shared/queries/useOrderPricing';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';
import { ItemPaymentBreakdown } from './ItemPaymentBreakdown';
import { PricingBreakdownOrderLineItem } from './PricingBreakdownOrderLineItem';

export type PricingBreakdownPrescription = { id: number; medication_name: string; product_quantity: string };

type Props = {
  readonly prescriptions: Prescription[] | PricingBreakdownPrescription[];
  readonly orderPricing?: OrderPricingQueryData;
  readonly forSinglePrescription?: boolean;
  readonly origin: string;
  readonly eventUuid: string;
};

const LineItemsContainer = styled(View)`
  border-color: ${() => COLORS.BORDER_COLORS.LIGHT};
  border-left-width: ${BORDERS.SIZE * 2}px;
  padding-left: ${SPACING.STATIC.MD.px};
`;

const initialLineItemHash = {
  alto_credit: null,
  alto_savings: null,
  cash_price: null,
  delivery_fee: null,
  essentials_only_fee: null,
  insurance_paid: null,
  on_demand_fee: null,
  same_day_delivery_fee: null,
  next_day_delivery_fee: null,
  pickup: null,
  standard_delivery_fee: null,
  asap_delivery_fee: null,
  next_day_mail_fee: null,
  standard_mail_fee: null,
  tip_amount: null,
  total: null,
  pending_progyny_coverage: null,
  total_program_dispensing_fee: null,
};

const DELIVERY_FEE_KEYS = new Set([
  'delivery_fee',
  'asap_delivery_fee',
  'next_day_delivery_fee',
  'same_day_delivery_fee',
  'standard_delivery_fee',
  'next_day_mail_fee',
  'standard_mail_fee',
]);
const isDeliveryFee = (lineItem: LineItem | null) => {
  if (!lineItem) return false;

  return DELIVERY_FEE_KEYS.has(lineItem.key);
};

export const PaymentBreakdown = ({
  forSinglePrescription = false,
  prescriptions,
  orderPricing,
  origin,
  eventUuid,
}: Props) => {
  const prescriptionPricing = useSelectorShared(getPrescriptionPricing);
  const orderDate = useSelectorShared(getOrderDate);
  const isCartOrCheckout = ['Cart', 'Checkout'].includes(origin);

  const lineItemHash = (orderPricing?.line_items || []).reduce<Record<LineItemType, LineItem | null>>(
    (acc, lineItem) => {
      acc[lineItem.key] = lineItem;
      return acc;
    },
    { ...initialLineItemHash },
  );
  const deliveryFeeLineItems = TypescriptUtils.objectValues(lineItemHash)
    .filter(isDeliveryFee)
    .filter(TypescriptUtils.isPresent);

  // In cart/checkout, delivery fee line items are only shown once an order date is selected. At that point:
  // - If the cart has a scheduleable shipment fee, the specific delivery fee line item is shown
  // - If not, the generic delivery fee line item is shown
  // Outside of cart/checkout (e.g. viewing an existing order's details), the current checkout date
  // isn't relevant, so delivery fee line items should always be shown.
  const showDeliveryFeeLineItems = !isCartOrCheckout || !!orderDate;

  return (
    <>
      {prescriptions.map((prescription) => {
        let pricing = orderPricing?.item_pricing?.[prescription.id];
        if (forSinglePrescription) {
          pricing = prescriptionPricing[prescription.id];
        }

        return (
          <Fragment key={prescription?.id}>
            <ListItem
              title={prescription?.medication_name}
              descriptions={[
                <ListDescription key={prescription?.medication_name}>
                  {prescription?.product_quantity?.toLocaleLowerCase()}
                </ListDescription>,
              ]}
              fullBleed
            />
            <SmSpacing />
            <LineItemsContainer>
              {pricing ? (
                <ItemPaymentBreakdown
                  pricingItem={pricing}
                  origin={origin}
                  eventUuid={eventUuid}
                />
              ) : null}
            </LineItemsContainer>
            <LgSpacing />
          </Fragment>
        );
      })}
      {orderPricing && !forSinglePrescription ? (
        <>
          <Separator />
          {lineItemHash.essentials_only_fee ? (
            <PricingBreakdownOrderLineItem lineItem={lineItemHash.essentials_only_fee} />
          ) : null}
          {showDeliveryFeeLineItems &&
            deliveryFeeLineItems.map((lineItem) => {
              return (
                <PricingBreakdownOrderLineItem
                  key={lineItem.key}
                  lineItem={lineItem}
                />
              );
            })}
          {lineItemHash.pickup ? <PricingBreakdownOrderLineItem lineItem={lineItemHash.pickup} /> : null}
          {lineItemHash.tip_amount?.value ? <PricingBreakdownOrderLineItem lineItem={lineItemHash.tip_amount} /> : null}
          {lineItemHash.total ? (
            <PricingBreakdownOrderLineItem
              lineItem={lineItemHash.total}
              noSeparator
            />
          ) : null}
        </>
      ) : null}
    </>
  );
};
